/**
 * The world model: a collection of airports (each with runways and navaids)
 * authored geographically, plus the projection that places them in the sim's
 * local NED frame.
 *
 * Design: features are authored in lat/lon and PROJECTED into the flat-NED
 * `RunwayDef` shape that Navigation/Runway/Scene already consume, so the rest of
 * the app keeps running in local metres and barely changes. One shared datum
 * anchors every airport in a single local plane — fine while airports are within
 * tens of NM of each other (see LocalProjection's re-anchoring note).
 */
import { GeoPoint, LocalNE, LocalProjection } from './Geo';
import { TerrainProvider, FlatTerrain } from './Terrain';
import { RunwayDef } from './Airport';

/** A runway authored geographically — the source form that derives a RunwayDef. */
export interface RunwayGeo {
  ident: string;
  headingDeg: number; // landing direction, true
  length: number; // m
  width: number; // m
  // Approach-end threshold; its elevation is the runway surface MSL height.
  thresholdGeo: GeoPoint;
  ils: RunwayDef['ils'];
}

/** Radio-navigation aid. Structural seam — no instrument consumes these yet. */
export type NavaidType = 'VOR' | 'NDB' | 'DME' | 'VORDME' | 'FIX';

export interface NavaidDef {
  ident: string;
  type: NavaidType;
  freq?: number; // MHz (VOR/DME) or kHz (NDB); undefined for a plain FIX
  position: GeoPoint;
  local: LocalNE; // projected NED position (filled in by the World builder)
}

/** An airport: reference point, its runways (derived flat-NED), and navaids. */
export interface AirportDef {
  ident: string;
  name: string;
  arp: GeoPoint; // airport reference point
  runways: RunwayDef[]; // DERIVED — the shape the renderer/nav consume
  navaids: NavaidDef[];
  terrain: TerrainProvider;
}

/** Geographic authoring form for one airport, before projection. */
export interface AirportGeo {
  ident: string;
  name: string;
  arp: GeoPoint;
  runways: RunwayGeo[];
  navaids?: Omit<NavaidDef, 'local'>[];
}

/** Project a geographically-authored runway into the flat-NED RunwayDef shape. */
export function deriveRunway(proj: LocalProjection, g: RunwayGeo): RunwayDef {
  const DEG = Math.PI / 180;
  const dir = { n: Math.cos(g.headingDeg * DEG), e: Math.sin(g.headingDeg * DEG) };
  return {
    ident: g.ident,
    headingDeg: g.headingDeg,
    length: g.length,
    width: g.width,
    elevation: g.thresholdGeo.elevation,
    dir,
    threshold: proj.geoToLocal(g.thresholdGeo),
    ils: g.ils,
  };
}

/** Project a whole airport (runways + navaids) into local NED. */
export function deriveAirport(proj: LocalProjection, g: AirportGeo): AirportDef {
  const runways = g.runways.map((r) => deriveRunway(proj, r));
  const navaids = (g.navaids ?? []).map((nv) => ({
    ...nv,
    local: proj.geoToLocal(nv.position),
  }));
  // Flat terrain at the (primary) runway's elevation until a real DEM exists.
  const terrain = new FlatTerrain(runways[0]?.elevation ?? g.arp.elevation);
  return { ident: g.ident, name: g.name, arp: g.arp, runways, navaids, terrain };
}

/**
 * Container for the projected airports plus the shared projection. The "active"
 * airport is the one nav/terrain/start-pose target; callers switch it by ident.
 */
export class World {
  readonly proj: LocalProjection;
  readonly airports: AirportDef[];
  activeIdent: string;

  constructor(readonly origin: GeoPoint, airportsGeo: AirportGeo[]) {
    this.proj = new LocalProjection(origin);
    this.airports = airportsGeo.map((a) => deriveAirport(this.proj, a));
    this.activeIdent = this.airports[0]?.ident ?? '';
  }

  get active(): AirportDef {
    return this.airports.find((a) => a.ident === this.activeIdent) ?? this.airports[0];
  }

  /** The active airport's primary (first) runway — the current nav target. */
  get activeRunway(): RunwayDef {
    return this.active.runways[0];
  }

  airport(ident: string): AirportDef | undefined {
    return this.airports.find((a) => a.ident === ident);
  }
}
